/**
 * Collapse consecutive whitespace (spaces, tabs, NBSP, etc.) to a single space and trim.
 * Use when persisting or geocoding address parts so accidental double spaces never reach storage/maps.
 */
export function normalizeAddressWhitespace(value: string | null | undefined): string {
  if (value === null || value === undefined || typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
}

/** Zod preprocess: collapse whitespace before min/regex checks (whitespace-only → empty). */
export function preprocessAddressWhitespace(value: unknown): unknown {
  return typeof value === 'string' ? normalizeAddressWhitespace(value) : value;
}

export type AddressPartsInput = {
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  name?: string | null;
};

export type NormalizedAddressParts = {
  street: string;
  postalCode: string;
  city: string;
  name: string;
};

/** Normalize street / postal / city / optional name for save payloads. */
export function normalizeAddressParts(parts: AddressPartsInput): NormalizedAddressParts {
  return {
    street: normalizeAddressWhitespace(parts.street),
    postalCode: normalizeAddressWhitespace(parts.postalCode),
    city: normalizeAddressWhitespace(parts.city),
    name: normalizeAddressWhitespace(parts.name),
  };
}

/**
 * Build a Nominatim-friendly "Street, 00100 City" query from parts (or a full line).
 * Always collapses internal whitespace so legacy rows with double spaces still geocode.
 */
export function formatGeocodeAddressQuery(
  streetOrFull?: string | null,
  postalCode?: string | null,
  city?: string | null,
): string {
  if (postalCode != null || city != null) {
    const street = normalizeAddressWhitespace(streetOrFull);
    const postal = normalizeAddressWhitespace(postalCode);
    const cityPart = normalizeAddressWhitespace(city);
    const locality = [postal, cityPart].filter(Boolean).join(' ');
    return [street, locality].filter(Boolean).join(', ');
  }
  return normalizeAddressWhitespace(streetOrFull);
}
